import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ImageItem } from "@/types/minds-eye";
import { CheckCircle2, ChevronLeft, ChevronRight, X, Download, Loader2, Save } from "lucide-react";
import { generateSlideshowVideo } from "@/utils/slideshowVideoGenerator";
import { Progress } from "@/components/ui/progress";
import VideoSettingsPanel, { type VideoSettings } from "@/components/VideoSettingsPanel";
import SpotifyMusicPanel from "@/components/spotify/SpotifyMusicPanel";

interface MindsEyeCycleProps {
  isOpen: boolean;
  onClose: () => void;
  images: ImageItem[];
  onComplete: (imagesWithGratitude: ImageItem[]) => void;
  onCreateVideo: () => void;
  onSaveVideoToBoard?: (videoBlob: Blob) => Promise<void>;
}

const MindsEyeCycle: React.FC<MindsEyeCycleProps> = ({ 
  isOpen, 
  onClose, 
  images, 
  onComplete,
  onCreateVideo,
  onSaveVideoToBoard
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [gratitudeText, setGratitudeText] = useState("");
  const [imagesWithGratitude, setImagesWithGratitude] = useState<ImageItem[]>([]);
  const [cycleComplete, setCycleComplete] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Video generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isSavingToBoard, setIsSavingToBoard] = useState(false);
  const [savedToBoard, setSavedToBoard] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (images.length > 0) {
      setImagesWithGratitude(
        images.map(img => ({
          ...img,
          gratitudeStatement: img.gratitudeStatement || ""
        }))
      );
    }
  }, [images]);

  // Reset all cycle/video state when the modal is (re)opened
  useEffect(() => {
    if (!isOpen) return;
    setCurrentIndex(0);
    setGratitudeText("");
    setCycleComplete(false);
    setIsTyping(false);
    setShowControls(true);
    setShowSettings(false);
    setIsGenerating(false);
    setGenerationProgress(0);
    setVideoBlob(null);
    setVideoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setIsSavingToBoard(false);
    setSavedToBoard(false);
  }, [isOpen]);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  // Auto-hide controls after inactivity
  useEffect(() => {
    if (cycleComplete) return;

    const resetTimer = () => {
      setShowControls(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        if (!isTyping) setShowControls(false);
      }, 3000);
    };

    resetTimer();
    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("touchstart", resetTimer);

    return () => {
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("touchstart", resetTimer);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [cycleComplete, isTyping]);

  const currentImage = images[currentIndex];

  const handleGratitudeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setGratitudeText(e.target.value);
    setIsTyping(e.target.value.length > 0);
  };

  const handleSubmitGratitude = () => {
    const updatedImages = [...imagesWithGratitude];
    updatedImages[currentIndex] = {
      ...updatedImages[currentIndex],
      gratitudeStatement: gratitudeText
    };
    setImagesWithGratitude(updatedImages);
    
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setGratitudeText(updatedImages[currentIndex + 1].gratitudeStatement || "");
      setIsTyping(!!(updatedImages[currentIndex + 1].gratitudeStatement));
    } else {
      setCycleComplete(true);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setGratitudeText(imagesWithGratitude[currentIndex - 1].gratitudeStatement || "");
      setIsTyping(!!(imagesWithGratitude[currentIndex - 1].gratitudeStatement));
    }
  };

  const handleSkip = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setGratitudeText(imagesWithGratitude[currentIndex + 1].gratitudeStatement || "");
      setIsTyping(!!(imagesWithGratitude[currentIndex + 1].gratitudeStatement));
    } else {
      setCycleComplete(true);
    }
  };

  const [showSettings, setShowSettings] = useState(false);

  const handleGenerateVideo = async (settings: VideoSettings) => {
    setIsGenerating(true);
    setGenerationProgress(0);
    try {
      const blob = await generateSlideshowVideo(
        imagesWithGratitude,
        {
          durationPerSlide: settings.durationPerSlide,
          fadeDuration: 1,
          loops: settings.loops,
          audioBlob: settings.audioBlob,
        },
        (progress) => setGenerationProgress(Math.round(progress))
      );
      setVideoBlob(blob);
      const url = URL.createObjectURL(blob);
      setVideoUrl(url);
    } catch (err) {
      console.error("Error generating slideshow video:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadVideo = () => {
    if (!videoBlob) return;
    const ext = videoBlob.type.includes("mp4") ? "mp4" : "webm";
    const a = document.createElement("a");
    a.href = URL.createObjectURL(videoBlob);
    a.download = `gratitude-slideshow.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleSaveToBoard = async () => {
    if (!videoBlob || !onSaveVideoToBoard) return;
    setIsSavingToBoard(true);
    try {
      await onSaveVideoToBoard(videoBlob);
      setSavedToBoard(true);
    } catch (err) {
      console.error("Error saving video to board:", err);
    } finally {
      setIsSavingToBoard(false);
    }
  };

  const handleComplete = () => {
    onComplete(imagesWithGratitude);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex flex-col bg-background ${!cycleComplete && !showControls ? 'cursor-none' : ''}`} >
      {/* Spotify soundtrack control */}
      <div
        className={`absolute top-4 right-16 z-20 transition-opacity duration-500 ${
          cycleComplete || showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <SpotifyMusicPanel position="header" returnTo="/dashboard/gratitude" />
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        className={`absolute top-4 right-4 z-10 rounded-full p-2 bg-background/60 backdrop-blur-sm border border-border text-muted-foreground hover:text-foreground transition-all duration-500 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <X className="h-5 w-5" />
      </button>


      {!cycleComplete ? (
        <>
          {/* Progress indicator */}
          <div className={`absolute top-4 left-4 z-10 text-sm text-muted-foreground bg-background/60 backdrop-blur-sm rounded-full px-3 py-1 border border-border transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {currentIndex + 1} / {images.length}
          </div>

          {/* Full-screen image */}
          <div className="absolute inset-0">
            {currentImage?.type === "video" ? (
              <video
                src={currentImage?.url}
                muted
                autoPlay
                loop
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={currentImage?.url}
                alt={currentImage?.name}
                className="w-full h-full object-cover"
              />
            )}
            {/* Gradient overlay - also fades with controls */}
            <div className={`absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/60 to-transparent pointer-events-none transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-0'}`} />
          </div>

          {/* Bottom panel - floating over image */}
          <div className={`absolute inset-x-0 bottom-0 px-4 pb-6 pt-4 space-y-4 z-10 transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <Textarea
              id="gratitude"
              value={gratitudeText}
              onChange={handleGratitudeChange}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitGratitude();
                }
              }}
              placeholder="Write a message about this image from the perspective of already having it in your life…"
              className={`min-h-[80px] resize-none border transition-all duration-300 text-white placeholder:text-white/50 ${
                isTyping
                  ? "backdrop-blur-xl bg-white/10 border-white/20 shadow-[0_4px_12px_-2px_rgba(0,0,0,0.3)]"
                  : "bg-transparent border-transparent"
              }`}
            />

            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
                <Button variant="ghost" onClick={handleSkip} className="text-white/70 hover:text-white hover:bg-white/10">
                  Skip
                </Button>
              </div>
              <Button className="cosmic-button" onClick={handleSubmitGratitude}>
                {currentIndex < images.length - 1 ? (
                  <>
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </>
                ) : (
                  <>
                    Complete
                    <CheckCircle2 className="ml-1 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center px-4 overflow-y-auto">
          <div className="text-center max-w-lg w-full py-8">
            {!videoUrl && !isGenerating && !showSettings && (
              <>
                <CheckCircle2 className="h-16 w-16 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  You've completed your Mind's Eye Cycle
                </h3>
                <p className="text-muted-foreground mb-6">
                  Create an immersive slideshow video with your images and gratitude statements displayed on screen.
                </p>
                <div className="flex justify-center space-x-4">
                  <Button variant="outline" onClick={handleComplete}>
                    Just Close
                  </Button>
                  <Button className="cosmic-button" onClick={() => setShowSettings(true)}>
                    Create Video
                  </Button>
                </div>
              </>
            )}

            {showSettings && !isGenerating && !videoUrl && (
              <VideoSettingsPanel
                onGenerate={handleGenerateVideo}
                onBack={() => setShowSettings(false)}
                isGenerating={isGenerating}
                imageCount={imagesWithGratitude.length}
              />
            )}

            {isGenerating && (
              <>
                <Loader2 className="h-12 w-12 text-primary mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  Creating your slideshow…
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Rendering {imagesWithGratitude.length} images with your gratitude statements
                </p>
                <Progress value={generationProgress} className="w-full max-w-xs mx-auto" />
                <p className="text-muted-foreground text-xs mt-2">{generationProgress}%</p>
              </>
            )}

            {videoUrl && !isGenerating && (
              <>
                <h3 className="text-lg font-medium text-foreground mb-4">
                  Your Gratitude Slideshow
                </h3>
                <div className="rounded-lg overflow-hidden border border-border mb-6">
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    autoPlay
                    playsInline
                    className="w-full aspect-video bg-black"
                  />
                </div>
                <div className="flex justify-center space-x-4 flex-wrap gap-2">
                  <Button variant="outline" onClick={handleDownloadVideo}>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </Button>
                  {onSaveVideoToBoard && (
                    <Button 
                      variant="outline" 
                      onClick={handleSaveToBoard}
                      disabled={isSavingToBoard || savedToBoard}
                    >
                      {isSavingToBoard ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                      ) : savedToBoard ? (
                        <><CheckCircle2 className="mr-2 h-4 w-4" />Saved to Board</>
                      ) : (
                        <><Save className="mr-2 h-4 w-4" />Save to Vision Board</>
                      )}
                    </Button>
                  )}
                  <Button className="cosmic-button" onClick={handleComplete}>
                    Done
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MindsEyeCycle;
